"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.themeEntitlement = themeEntitlement;
exports.renderableTheme = renderableTheme;
exports.paidThemeNames = paidThemeNames;
exports.entitledThemes = entitledThemes;
const tokens_1 = require("./tokens");
const DAY_MS = 86_400_000;
/**
 * 单套主题的权益。
 *
 * 「按时间判而不是按 status 判」是这里最要紧的一条：`status` 由定时扫描推进，
 * 天然落后于真实时间最多一个扫描周期。如果渲染层信 `status`，就会出现
 * 客户刚续完费、站却还是降级状态（等下一轮扫描才恢复），
 * 或者过期了却因为扫描没跑到而继续白用。**时间戳不会滞后**。
 *
 * 但 `canceled` / `expired` 例外：那是运营强停或退款，语义就是"立刻不能用"，
 * 不能等到 period_end 才生效，否则退款客户还能再用一个月。
 */
function themeEntitlement(themeName, subscriptions, now = new Date()) {
    const manifest = tokens_1.THEMES[themeName];
    const monthlyCents = manifest?.pricing?.monthlyCents ?? 0;
    // 未知主题：调用方本该先 isKnownTheme 拦掉，走到这里说明数据脏了。
    // 返回"不可用"而不是抛错 —— 一个脏的主题名不该让整页 500。
    if (!manifest) {
        return {
            theme: themeName,
            paid: false,
            entitled: false,
            reason: 'never',
            validUntil: null,
            daysLeft: null,
            monthlyCents: 0,
        };
    }
    // 免费（含"忘了填 pricing"）一律可用。基础三套必须无门槛：
    // 客户退订付费主题后落回的就是它们，它们有任何门槛就等于把客户关在门外
    if (monthlyCents <= 0) {
        return {
            theme: themeName,
            paid: false,
            entitled: true,
            reason: 'free',
            validUntil: null,
            daysLeft: null,
            monthlyCents: 0,
        };
    }
    const sub = subscriptions.find((s) => s.themeName === themeName);
    if (!sub) {
        return {
            theme: themeName,
            paid: true,
            entitled: false,
            reason: 'never',
            validUntil: null,
            daysLeft: null,
            monthlyCents,
        };
    }
    const base = {
        theme: themeName,
        paid: true,
        monthlyCents,
    };
    // 强停/退款：立即失效，不看时间
    if (sub.status === 'canceled' || sub.status === 'expired') {
        return {
            ...base,
            entitled: false,
            reason: 'expired',
            validUntil: sub.currentPeriodEnd,
            daysLeft: Math.floor((sub.currentPeriodEnd.getTime() - now.getTime()) / DAY_MS),
        };
    }
    const end = sub.currentPeriodEnd.getTime();
    const grace = sub.graceUntil ? sub.graceUntil.getTime() : end;
    const t = now.getTime();
    if (t <= end) {
        return {
            ...base,
            entitled: true,
            reason: 'subscribed',
            validUntil: sub.currentPeriodEnd,
            daysLeft: Math.floor((end - t) / DAY_MS),
        };
    }
    if (t <= grace) {
        return {
            ...base,
            entitled: true,
            reason: 'grace',
            validUntil: sub.graceUntil ?? sub.currentPeriodEnd,
            daysLeft: Math.floor((grace - t) / DAY_MS),
        };
    }
    return {
        ...base,
        entitled: false,
        reason: 'expired',
        validUntil: sub.graceUntil ?? sub.currentPeriodEnd,
        daysLeft: Math.floor((grace - t) / DAY_MS),
    };
}
/**
 * 该主题此刻能拿什么去渲染。
 *
 * 无权就回退到基础主题，**不改库**：库里存的仍是客户买过的那套，
 * 下次订阅恢复他不用重新选一遍。渲染层做这件事，是为了让"钱断了"
 * 不表现为站点 500 或一片错乱 —— 客户站是给老外看的，页面必须永远正常。
 */
function renderableTheme(themeName, subscriptions, now = new Date()) {
    if (!themeName || !tokens_1.THEMES[themeName]) {
        return { theme: tokens_1.DEFAULT_THEME, downgraded: Boolean(themeName), entitlement: null };
    }
    const entitlement = themeEntitlement(themeName, subscriptions, now);
    if (entitlement.entitled) {
        return { theme: themeName, downgraded: false, entitlement };
    }
    return { theme: tokens_1.DEFAULT_THEME, downgraded: true, entitlement };
}
/** 需要付费才能用的主题名。市场页与"锁"的显示都从这里取 */
function paidThemeNames() {
    return Object.values(tokens_1.THEMES)
        .filter((m) => (m.pricing?.monthlyCents ?? 0) > 0)
        .map((m) => m.name);
}
/**
 * 客户手里此刻真正能用的主题集合。
 *
 * 注意它返回**主题名数组**而不是过滤后的 manifest：控制台要拿它去
 * 跟完整清单做差集，才能画出"这一套要订阅"的锁卡片。
 */
function entitledThemes(subscriptions, now = new Date()) {
    return Object.keys(tokens_1.THEMES).filter((name) => themeEntitlement(name, subscriptions, now).entitled);
}
//# sourceMappingURL=entitlement.js.map